import { home } from "@/content";
import { Rich, toneClass } from "@/lib/rich";

const ICON_TONE: Record<string, string> = {
  green: "text-green-600 dark:text-green-400",
  amber: "text-amber-600 dark:text-amber-500",
  stone: "text-stone-600 dark:text-white/60",
  slate: "text-slate-500 dark:text-white/60",
  blue: "text-blue-600 dark:text-blue-400",
};

export default function LightHero() {
  const { hero } = home;

  return (
    <section id="home" className="relative min-h-screen flex items-center bg-[#FAFAF8] dark:bg-[#0A0A0A] overflow-hidden pt-20">
      {/* Мягкие тёплые пятна */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] right-[5%] w-[550px] h-[550px] rounded-full bg-orange-200/30 dark:bg-orange-500/[0.12] blur-[140px]" />
        <div className="absolute bottom-[10%] left-[-5%] w-[450px] h-[450px] rounded-full bg-amber-200/25 dark:bg-amber-500/10 blur-[120px]" />
        <div className="absolute top-[35%] left-[10%] w-[400px] h-[400px] rounded-full bg-green-300/25 dark:bg-green-500/10 blur-[130px]" />
        <div className="absolute bottom-[15%] right-[5%] w-[450px] h-[450px] rounded-full bg-violet-300/25 dark:bg-violet-500/10 blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-24 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-14 lg:gap-20">
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-stone-100 dark:bg-white/[0.06] border border-stone-200 dark:border-white/[0.10] text-stone-600 dark:text-white/70 text-xs font-semibold mb-7">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
              {hero.badge}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-extrabold text-stone-900 dark:text-white leading-[1.08] mb-6 tracking-tight">
              <Rich text={hero.title} accentClassName="text-orange-700 dark:text-orange-400" />
            </h1>

            <p className="text-stone-600 dark:text-white/65 text-lg md:text-xl leading-relaxed mb-4 max-w-xl mx-auto lg:mx-0">
              {hero.lead}
            </p>
            <p className="text-stone-600 dark:text-white/65 text-base leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0">
              {hero.text}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href={hero.primaryCta.href}
                className="px-8 py-4 rounded-xl bg-stone-900 dark:bg-orange-500 text-white dark:text-[#0A0A0A] font-bold text-base hover:bg-stone-800 dark:hover:bg-orange-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                {hero.primaryCta.label}
              </a>
              <a
                href={hero.secondaryCta.href}
                className="px-8 py-4 rounded-xl border border-stone-300 dark:border-white/20 text-stone-700 dark:text-white font-semibold text-base hover:border-stone-400 dark:hover:border-white/30 hover:bg-stone-50 dark:hover:bg-white/[0.06] transition-colors cursor-pointer whitespace-nowrap"
              >
                {hero.secondaryCta.label}
              </a>
            </div>

            <div className="mt-14 flex flex-wrap gap-8 justify-center lg:justify-start">
              {hero.stats.map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="text-2xl font-extrabold text-stone-900 dark:text-white">
                    {stat.value}
                  </div>
                  <div className="text-stone-600 dark:text-white/65 text-xs mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Превью воронки */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[480px]">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-200/40 dark:from-orange-500/20 to-amber-200/30 dark:to-amber-500/15 blur-[60px] rounded-3xl scale-95" />
              <div className="relative bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/[0.08] dark:backdrop-blur-xl rounded-3xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400 dark:bg-white/20"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-400 dark:bg-white/20"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400 dark:bg-white/20"></div>
                  </div>
                  <span className="text-stone-600 dark:text-white/65 text-xs font-medium">
                    {hero.preview.caption}
                  </span>
                </div>

                <div className="space-y-3 mb-5">
                  {hero.preview.stages.map((stage) => (
                    <div key={stage.stage}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-stone-600 dark:text-white/65 text-xs">{stage.stage}</span>
                        <span className="text-stone-900 dark:text-white font-bold text-xs">
                          {stage.count}
                        </span>
                      </div>
                      <div className="h-2 bg-stone-100 dark:bg-white/[0.06] rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${toneClass(
                            stage.tone,
                            "bg-stone-800"
                          )}`}
                          style={{ width: `${stage.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {hero.preview.metrics.map((metric) => (
                    <div
                      key={metric.label}
                      className="bg-stone-50 dark:bg-white/[0.04] rounded-xl p-3 text-center"
                    >
                      <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
                        <i
                          className={`${metric.icon} ${
                            ICON_TONE[metric.tone] ?? "text-stone-600 dark:text-white/60"
                          } text-sm`}
                        ></i>
                      </div>
                      <div className="text-stone-900 dark:text-white font-bold text-sm">
                        {metric.value}
                      </div>
                      <div className="text-stone-600 dark:text-white/65 text-[10px]">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
